import { useMemo } from 'react';
import { create } from '@bufbuild/protobuf';
import { useQuery } from '@tanstack/react-query';
import { App, Button, Tag, Tooltip, type TableColumnsType } from 'antd';
import {
  CircleCheck,
  CirclePause,
  CircleAlert,
  Pause,
  Play,
  RefreshCw,
  Trash2,
} from 'lucide-react';
import DsIcon from '@/components/Layout/DsIcon';
import ProTable from '@/components/pro/ProTable';
import { applicationApi } from '@/api/application.api';
import { channelApi } from '@/api/channel.api';
import { ApiError } from '@/api/errors';
import { UserName } from '@/components/UserName';
import { formatExpire, formatTimestamp } from '@/utils/utils';
import { ListRequestSchema } from '@/proto/agentplatform/httpv2/common_pb';
import { ListChannelAccountsRequestSchema } from '@/proto/agentplatform/httpv2/channel_pb';
import { ChannelType } from '@/proto/agentplatform/v1/channel_pb';
import { TokenStatus, TokenType, type AgentToken } from '@/proto/agentplatform/v2/token_pb';
import {
  useDeleteAgentToken,
  useRegenerateAgentToken,
  useToggleAgentToken,
} from '@/modules/tokens';

interface Props {
  data: AgentToken[];
  loading: boolean;
  tokenType: TokenType;
  /** 重置后回调，调用方需弹 TokenRevealDialog 展示新 token。 */
  onRegenerated: (data: { name: string; token: string }) => void;
}

export default function TokenTable({ data, loading, tokenType, onRegenerated }: Props) {
  const { message, modal } = App.useApp();
  const toggleMut = useToggleAgentToken(tokenType);
  const regenMut = useRegenerateAgentToken(tokenType);
  const deleteMut = useDeleteAgentToken(tokenType);

  // 名称查找：Token 列表里只有 ID，需要查应用/渠道账号名做 Tag 展示。
  const { data: appsRes } = useQuery({
    queryKey: ['applications', 'list', 'token-display'],
    queryFn: () => applicationApi.list(create(ListRequestSchema, { page: 1, pageSize: 100 })),
  });
  const { data: chRes } = useQuery({
    queryKey: ['channel-accounts', 'list', 'token-display', ChannelType.BRIDGE],
    queryFn: () =>
      channelApi.listChannelAccounts(
        create(ListChannelAccountsRequestSchema, {
          page: 1,
          pageSize: 100,
          type: ChannelType.BRIDGE,
        })
      ),
    enabled: tokenType === TokenType.WEBSOCKET,
  });

  const appsById = useMemo(() => {
    const m = new Map<string, string>();
    for (const a of appsRes?.data ?? []) m.set(a.id, a.name);
    return m;
  }, [appsRes]);
  const wsById = useMemo(() => {
    const m = new Map<string, string>();
    for (const a of chRes?.items ?? []) m.set(a.id, a.name);
    return m;
  }, [chRes]);

  async function onToggle(t: AgentToken) {
    try {
      await toggleMut.mutateAsync({ id: t.id, enable: t.status !== TokenStatus.ENABLED });
      message.success(t.status === TokenStatus.ENABLED ? 'Token 已停用' : 'Token 已启用');
    } catch (e) {
      message.error((e as Error).message);
    }
  }

  function onRegen(t: AgentToken) {
    modal.confirm({
      title: '重置 Token',
      content: `重置 Token「${t.name}」？之前的 token 立即失效，使用方需替换。`,
      okText: '重置',
      async onOk() {
        try {
          const res = await regenMut.mutateAsync(t.id);
          onRegenerated({ name: res.name, token: res.token });
        } catch (e) {
          message.error((e as Error).message);
        }
      },
    });
  }

  function onDelete(t: AgentToken) {
    if (t.status === TokenStatus.ENABLED) {
      message.error('请先停用再删除');
      return;
    }
    modal.confirm({
      title: '删除 Token',
      content: `确认删除 Token「${t.name}」？`,
      okType: 'danger',
      async onOk() {
        try {
          await deleteMut.mutateAsync(t.id);
          message.success('Token 已删除');
        } catch (e) {
          message.error(e instanceof ApiError ? e.message : (e as Error).message);
        }
      },
    });
  }

  const columns: TableColumnsType<AgentToken> = [
    {
      title: '名称',
      width: 180,
      key: 'name',
      render: (_, r) => <div className="name-text">{r.name}</div>,
    },
    {
      title: 'Token',
      width: 180,
      key: 'token',
      render: (_, r) => <code className="pp-mono">{r.token}</code>,
    },
  ];

  if (tokenType === TokenType.API) {
    columns.push({
      title: '绑定 Agent 实例',
      width: 200,
      key: 'bound',
      render: (_, r) => (
        <Tag
          color="processing"
          bordered={false}
          style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}
        >
          <DsIcon name="boxes" size={11} /> {appsById.get(r.agentInstanceId) || r.agentInstanceId}
        </Tag>
      ),
    });
  } else {
    columns.push({
      title: '绑定渠道账号',
      width: 200,
      key: 'wsBound',
      render: (_, r) => (
        <Tag
          color="processing"
          bordered={false}
          style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}
        >
          <DsIcon name="cable" size={11} /> {wsById.get(r.channelAccountId) || r.channelAccountId}
        </Tag>
      ),
    });
  }

  columns.push(
    {
      title: '状态',
      key: 'status',
      width: 110,
      render: (_, r) => {
        const { color, text, icon } =
          r.status === TokenStatus.EXPIRED
            ? { color: 'error' as const, text: '已过期', icon: <CircleAlert size={12} /> }
            : r.status === TokenStatus.ENABLED
              ? { color: 'success' as const, text: '已启用', icon: <CircleCheck size={12} /> }
              : { color: 'default' as const, text: '已停用', icon: <CirclePause size={12} /> };
        return (
          <Tag
            style={{ display: 'inline-flex', alignItems: 'center', gap: 3 }}
            color={color}
            icon={icon}
          >
            {text}
          </Tag>
        );
      },
    },
    { title: '有效期', key: 'expireAt', minWidth: 160, render: (_, r) => formatExpire(r.expireAt) },
    {
      title: tokenType === TokenType.API ? '最近调用' : '最近连接',
      key: 'lastUsedAt',
      minWidth: 160,
      render: (_, r) => formatTimestamp(r.lastUsedAt),
    },
    {
      title: '创建者',
      key: 'userId',
      width: 160,
      render: (_, r) => <UserName userId={r.userId} />,
    },
    {
      title: '创建时间',
      key: 'createdAt',
      minWidth: 160,
      render: (_, r) => formatTimestamp(r.createdAt),
    },
    {
      title: '更新时间',
      key: 'updatedAt',
      minWidth: 160,
      render: (_, r) => formatTimestamp(r.updatedAt),
    },
    {
      title: '操作',
      key: '__actions',
      minWidth: 120,
      align: 'left',
      fixed: 'right',
      render: (_, r) => (
        <>
          <Tooltip
            title={
              r.status === TokenStatus.EXPIRED
                ? '已过期'
                : r.status === TokenStatus.ENABLED
                  ? '停用'
                  : '启用'
            }
          >
            <Button
              type="text"
              size="small"
              disabled={r.status === TokenStatus.EXPIRED}
              icon={r.status === TokenStatus.ENABLED ? <Pause size={14} /> : <Play size={14} />}
              onClick={() => onToggle(r)}
            />
          </Tooltip>
          <Tooltip title="重置">
            <Button
              type="text"
              size="small"
              icon={<RefreshCw size={14} />}
              onClick={() => onRegen(r)}
            />
          </Tooltip>
          <Tooltip title="删除">
            <Button
              type="text"
              size="small"
              icon={<Trash2 size={14} />}
              onClick={() => onDelete(r)}
            />
          </Tooltip>
        </>
      ),
    }
  );

  return (
    <ProTable<AgentToken>
      data={data}
      columns={columns}
      rowKey="id"
      size="middle"
      loading={loading}
      scroll={{ x: 'max-content' }}
      emptyText="还没有 Token"
    />
  );
}
